/**
 * A domain's retention window, as the console reads and writes it (`platform` ADR-090, ADR-091).
 *
 * The server holds one number per domain, `retention_days`: null follows the platform's window,
 * 0 keeps everything, and 1 to 3650 is the domain's own number of days from the call. The window
 * in force is the domain's own when it has one and the platform's when it has none; the server
 * sends both (`effective_retention_days`, `platform_retention_days`) and `inForce` mirrors the
 * rule only so the form can say what a choice would do before it is saved.
 *
 * Everything here is a pure function of those numbers, because the one question this form has to
 * get right is "does this change delete anything": a shorter window deletes the calls already
 * stored, within the hour, and that cannot be undone.
 */

export const MAX_RETENTION_DAYS = 3650;

/** The three things an admin can pick. `days` carries a number with it. */
export type RetentionChoice = "platform" | "keep" | "days";

export interface RetentionSetting {
  /** The domain's own window: null follows the platform's, 0 keeps everything. */
  retention_days: number | null;
  /** The window in force. 0 = everything is kept. */
  effective_retention_days: number;
  /** The platform's window, which a domain with no number of its own follows. */
  platform_retention_days: number;
}

export function choiceOf(own: number | null): RetentionChoice {
  if (own === null) return "platform";
  return own === 0 ? "keep" : "days";
}

/** The window in force for a domain whose own setting is `own`. */
export function inForce(own: number | null, platform: number): number {
  return Math.max(0, own ?? platform);
}

/** A whole number of days from 1 to 3650, or null for anything else: empty, a fraction, text. */
export function parseDays(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const days = Number(trimmed);
  return days >= 1 && days <= MAX_RETENTION_DAYS ? days : null;
}

/** What is wrong with the days field, in words, or undefined when it is a usable number. */
export function daysError(text: string): string | undefined {
  if (text.trim() === "") return "Enter a number of days.";
  if (parseDays(text) === null) return `Enter a whole number from 1 to ${MAX_RETENTION_DAYS}.`;
  return undefined;
}

/**
 * The `retention_days` a choice saves: null, 0 or the number typed. `undefined` when the choice
 * is "days" and the field does not hold a usable number, so there is nothing to save yet.
 */
export function ownFor(choice: RetentionChoice, daysText: string): number | null | undefined {
  if (choice === "platform") return null;
  if (choice === "keep") return 0;
  return parseDays(daysText) ?? undefined;
}

/**
 * Whether moving from one window in force to another deletes calls that are already stored: the
 * new window deletes at all, and it is shorter than the one before (or the one before kept
 * everything). A longer window, or keeping everything, deletes nothing.
 */
export function shortens(current: number, next: number): boolean {
  if (next <= 0) return false;
  return current <= 0 || next < current;
}

export function daysLabel(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/** The window in force in a few words: "Kept" or "90 days". */
export function windowLabel(days: number): string {
  return days <= 0 ? "Everything is kept" : `Deleted after ${daysLabel(days)}`;
}

/** Where a domain's window comes from, for the line under it. */
export function sourceLabel(own: number | null): string {
  return own === null ? "Follows the platform" : "This domain's own";
}

export function recordingsLabel(count: number): string {
  return count === 1 ? "1 recording" : `${count} recordings`;
}
